import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, TextField, MenuItem, Paper, Typography,
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, TableSortLabel,
  Chip, InputAdornment, IconButton, Grid, CircularProgress, Stack,
  Button
} from '@mui/material';
import { Search as SearchIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import axios from 'axios';
import configServ from "../../services/config";

import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
export const appServiceName = process.env.REACT_APP_BACKEND_URL;


const DepartmentTable = ({ fetchTableDataTrue, handleEdit }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
 const {
    
     company_id,
   } = useSelector((state) => state.GlobalVariables);
  const [params, setParams] = useState({
    search: '',
    status: '',
    sort_by: 'name', // Default sorting to name as requested
    sort_order: 'asc',
    per_page: 10,
    page: 1,
    company_id: company_id,
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const cleanParams = Object.fromEntries(
        Object.entries(params).filter(([_, v]) => v !== '')
      );
      const response = await axios.get(`${appServiceName}/api/departments`, {
        params: cleanParams
      });
      console.log("response", response)
      if (response.data.status) {
        setData(response.data.data);
        setTotal(response.data.total);
      }
    } catch (error) {
      console.error("Error fetching designations:", error);
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    fetchData();
  }, [fetchData, fetchTableDataTrue]); // Re-fetch when params or fetchTableDataTrue changes

  const handleRequestSort = (property) => {
    if (property !== 'name') return; // Disable sorting for others
    const isAsc = params.sort_by === property && params.sort_order === 'asc';
    setParams({ ...params, sort_by: property, sort_order: isAsc ? 'desc' : 'asc' });
  };

  const handleDelete = async (id) => {
    try {
      if (window.confirm("Are you sure you want to delete this department?")) {
        const response = await configServ.deleteDepartment(id);
        if (response?.status) {
          toast.success("Department deleted successfully!");
          fetchData(); // Refresh the table after deletion
        } else {
          toast.error(response?.message || "Failed to delete department.");
        }
      } else {
        toast.info("Deletion cancelled.");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "An error occurred while deleting.");
    }
  };

  return (
    <Paper sx={{ width: '100%', p: 2, mt: 2, position: 'relative' }}>
      <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
            Department List : {total}
          </Typography>
        </Grid>

        {/* Search and Status moved to Right side using Grid */}
        <Grid item xs={12} md={8}>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={params.search}
              onChange={(e) => setParams({ ...params, search: e.target.value, page: 1 })}
              sx={{ width: { xs: '100%', sm: 250 }, backgroundColor: '#fff' }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
              }}
            />
            <TextField
              select
              label="Status"
              size="small"
              sx={{ minWidth: 120, backgroundColor: '#fff' }}
              value={params.status}
              onChange={(e) => setParams({ ...params, status: e.target.value, page: 1 })}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </TextField>
          </Stack>
        </Grid>
      </Grid>

      <TableContainer sx={{ maxHeight: 500, position: 'relative' }}>
        {/* Loader Overlay */}
        {loading && (
          <Box sx={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.7)', zIndex: 2
          }}>
            <CircularProgress color="primary" />
          </Box>
        )}

        {/* ================= DESKTOP VIEW (Hidden on Mobile) ================= */}
        <Table
          stickyHeader
          size="small"
          sx={{ display: { xs: 'none', sm: 'table' } }} // Hides on mobile, shows on tablet/desktop
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ backgroundColor: '#1976d2', color: '#fff', fontWeight: 'bold' }}>
                S.No
              </TableCell>
              <TableCell sx={{ backgroundColor: '#1976d2', color: '#fff', fontWeight: 'bold' }}>
                <TableSortLabel
                  active={params.sort_by === 'name'}
                  direction={params.sort_by === 'name' ? params.sort_order : 'asc'}
                  onClick={() => handleRequestSort('name')}
                  sx={{
                    '&.MuiTableSortLabel-active': { color: '#fff' },
                    '& .MuiTableSortLabel-icon': { color: '#fff !important' }
                  }}
                >
                  Department Name
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ backgroundColor: '#1976d2', color: '#fff', fontWeight: 'bold' }}>
                Status
              </TableCell>
              <TableCell align="center" sx={{ backgroundColor: '#1976d2', color: '#fff', fontWeight: 'bold' }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, index) => (
              <TableRow key={row.id} hover>
                <TableCell>{(params.page - 1) * params.per_page + index + 1}</TableCell>
                <TableCell sx={{ fontWeight: 500 }}>{row.name}</TableCell>
                <TableCell>
                  <Chip
                    label={row.status}
                    size="small"
                    sx={{ textTransform: 'capitalize' }}
                    color={row.status === 'active' ? 'success' : 'error'}
                  />
                </TableCell>
                <TableCell align="center">
                  <Stack direction="row" spacing={1} justifyContent="center">
                    <IconButton size="small" color="primary" onClick={() => handleEdit(row)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* ================= MOBILE VIEW (Hidden on Desktop) ================= */}
        <Box sx={{ display: { xs: 'block', sm: 'none' }, p: 1 }}>
          {/* Mobile Sorting Option */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => handleRequestSort('name')}
              startIcon={params.sort_order === 'asc' ? '↑' : '↓'}
            >
              Sort by Name ({params.sort_order.toUpperCase()})
            </Button>
          </Box>

          {/* Mobile Cards */}
          {data.map((row, index) => (
            <Box
              key={row.id}
              sx={{
                backgroundColor: '#f5f9fc',
                borderRadius: '8px',
                p: 2,
                mb: 2,
                border: '1px solid #e0e0e0',
                boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="caption" color="textSecondary" fontWeight="bold">
                  S.No: {(params.page - 1) * params.per_page + index + 1}
                </Typography>
                <Chip
                  label={row.status}
                  size="small"
                  sx={{ textTransform: 'capitalize' }}
                  color={row.status === 'active' ? 'success' : 'error'}
                />
              </Stack>

              <Typography variant="body1" fontWeight="600" sx={{ mb: 2, color: '#1a3a5a' }}>
                {row.name}
              </Typography>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #e0e0e0', pt: 1 }}>
                <IconButton size="small" color="primary" onClick={() => handleEdit(row)}>
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          ))}
        </Box>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={total}
        rowsPerPage={params.per_page}
        page={params.page - 1}
        onPageChange={(e, newPage) => setParams({ ...params, page: newPage + 1 })}
        onRowsPerPageChange={(e) => setParams({ ...params, per_page: parseInt(e.target.value, 10), page: 1 })}
      />
    </Paper>
  );
};

export default DepartmentTable;